import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from '../types';

export const actOutTheLineSkill: Skill = {
    name: SKILL_NAMES.actOutTheLine,
    description:
        'Make the motion of a motion graphic show what the narration says happens: read each ' +
        'line for its action, then perform it with the element, on the verb, often as the ' +
        'handoff into the next beat. A lexicon of actions and the moves that show them. Load ' +
        'before planning a graphic whose narration says something rises, falls, joins, splits, ' +
        'overtakes or changes.',
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# Act Out the Line

In a motion graphic the motion is the explanation. When the voice says something happens,
the element on screen does it: it doesn't just arrive while the voice describes it.
The test is to mute the voice. The moves alone should still tell you what happened: up or
down, fast or slow, together or apart, who beat whom.

## Read the line first
Before planning a graphic, go through its narration and mark every word that says something
*happens*. Next to each one, note the gesture that shows it. Look for:
- **Direction**: up, down, forward, back, in, out, across.
- **Amount or size**: more, less, bigger, smaller, double, half.
- **Speed**: faster, slower, stalls, accelerates.
- **State**: breaks, opens, locks, flips, stops, starts, recovers.
- **Relationship**: one overtakes, replaces, joins, leaves, outweighs, pushes back on, or
  trails another.
- **Cause**: one thing sets another moving.
- **Steadiness**: unchanged, flat, holding. Standing still while everything else moves is a
  gesture too.

Comparatives and adjectives count as well as verbs: "a bigger share", "a faster close", "a
slimmer line-up". A plain name, label or amount with no action in its line just arrives on an
ordinary handoff. A metaphor forced onto a line that holds none is noise.

## It must look different from an ordinary arrival
The common failure is mapping every positive line to "the figure rises in from below". That
is the move every beat already arrives with, so nothing reads as acted. If you could swap the
acted move for the ordinary arrival and nobody would notice, it is not acted. The shape of the
move carries the meaning: its path, its rhythm, how it starts and how it stops. Its direction
alone does not.

## Strength: one direction, many moves
Words that share a direction differ in how hard and how fast. Give them different moves:

| Upward line says | Move |
|---|---|
| a little, slowly ("edged up", "crept higher") | a short lift on easy ease, no overshoot, nearly nothing |
| steadily ("increased", "went up") | a clean lift on the settle curve, arriving at rest |
| in stages ("step by step", "quarter after quarter") | two or three short lifts with tiny holds between, each landing with a small settle; it can tip a few degrees one way then the other on each step, like something hauling itself up |
| suddenly ("leapt", "spiked") | a crouch, a hop on an arc (\`${SKILL_NAMES.arcPathMotion}\`), a landing with squash if soft (\`${SKILL_NAMES.squashAndStretch}\`) |
| explosively ("soared", "took off", "shot up") | a pull-back and hold (\`${SKILL_NAMES.anticipationWindUp}\`), then a fast launch with a streak (\`${SKILL_NAMES.motionBlurStreak}\`), overshooting or leaving the frame |

| Downward line says | Move |
|---|---|
| a little ("dipped", "eased") | a small sink and settle, nearly still |
| steadily ("declined", "went down") | a steady lowering on easy ease |
| sliding ("slipped", "slid") | a diagonal slip down and sideways, decelerating, as if down a slope |
| suddenly ("dropped", "fell") | a drop from above that lands with weight (\`${SKILL_NAMES.massAndWeight}\`, medium or heavy) |
| hard ("plunged", "crashed", "collapsed") | a heavy fall with a hard contact; it can tip over a corner or crack into two pieces that fall apart |

## Lexicon: what the line says, and the move that shows it

| Meaning | Move | Built with |
|---|---|---|
| bigger, expands, spreads | grows from its own centre, or stretches along the axis that grew; "spreads" radiates out from a point | the settle curve; \`${SKILL_NAMES.squashAndStretch}\` for a soft overshoot |
| doubles, multiplies | a copy slides out of the original, so one becomes two (or more) side by side | the settle curve |
| smaller, cut, trimmed | squashes down or narrows; "cut" can slice a piece away that falls off | \`${SKILL_NAMES.massAndWeight}\` for the falling piece |
| faster, accelerates | crosses on an ease-in that keeps speeding up, leaning forward, with a streak at the peak | \`${SKILL_NAMES.motionBlurStreak}\`, \`${SKILL_NAMES.followThroughOverlap}\` |
| slower, stalls | decelerates to a stop, tips forward and settles back; a stall stops twice | \`${SKILL_NAMES.followThroughOverlap}\` |
| unchanged, flat, steady | arrives and stays perfectly still, after a beat that moved hard, so the stop reads | the settle curve |
| recovers, rebounds | dips, then springs back up past its rest and settles | \`${SKILL_NAMES.squashAndStretch}\` |
| breaks, fails | cracks, and the pieces fall apart | \`${SKILL_NAMES.massAndWeight}\` |
| launches, starts, opens | a wind-up, then a release; something inside opens or pops out | \`${SKILL_NAMES.anticipationWindUp}\`, \`${SKILL_NAMES.secondaryAction}\` |
| joins, merges, partners | two come in from opposite sides on arcs and meet in the centre, squashing on contact, then become one | \`${SKILL_NAMES.arcPathMotion}\`, \`${SKILL_NAMES.elementMorphHandoff}\` |
| splits, parts ways, diverges | starts as one or side by side in the centre, peels apart, and accelerates to opposite edges | the gather curve |
| overtakes, passes, beats | side by side; one accelerates past the other, which drops back | the gather and settle curves |
| replaces, takes over | the new one pushes the old out of its slot along one axis | \`${SKILL_NAMES.velocityMatchedCut}\` |
| pushes back, resists | leans into the other; both strain in small back-and-forth moves until one gives | \`${SKILL_NAMES.exaggeratedPoses}\` |
| outweighs, tips the balance | both sit on a beam that tips toward the heavier one | \`${SKILL_NAMES.massAndWeight}\` |
| ahead, in front, first | the leader out in front, the others trailing behind in a line, each lagging a little | \`${SKILL_NAMES.followThroughOverlap}\` |
| causes, triggers, sets off | the first hits or pushes the second, and the second moves because of it: contact, then reaction | \`${SKILL_NAMES.massAndWeight}\`, \`${SKILL_NAMES.secondaryAction}\` |
| forward, progress, later | travels right | |
| back, behind, earlier | travels left | |
| returns, comes back, cycles | goes out on an arc and comes back along it to its start | \`${SKILL_NAMES.arcPathMotion}\` |

The table is a starting point, not a menu to apply blindly. Read what this line means, then
find the move.

## Where the gesture goes
- **On a beat**: the element arrives, holds or exits doing it, whichever the words describe.
  Things that part ways can start together in the centre and leave for opposite edges.
- **Between two beats, which is the common case.** A line usually puts its verb between a
  subject and a figure ("subscribers doubled to 4 million"): the beats are the subject and
  the figure, and the verb is spoken between them. Perform it in the move from one to the
  other. The subject does the verb on its way out, and the figure arrives out of that move.
- **Best of all, that handoff is also a match cut or a morph**, so the subject acting out the
  verb *becomes* the figure:
  - **Velocity match**: the subject's gesture carries it off frame, and the figure enters on
    the same line at the same speed (\`${SKILL_NAMES.velocityMatchedCut}\`). "Downloads" crouches,
    launches up out of frame on "shot up", and "2M" drops back in along the same line.
  - **Shape match**: the gesture ends in a shape the figure starts from (a spinning element
    lands as the round 0 of the figure; a stretched word becomes a bar) (\`${SKILL_NAMES.shapeMatchCut}\`).
  - **Morph**: the subject's shape turns into the figure's while it is still performing: a
    drawn object reshaping into another (\`${SKILL_NAMES.facetMorphShape}\`), a word into the
    object it names (\`${SKILL_NAMES.typeAsObject}\`), or the badge around one label reshaping
    around the next (\`${SKILL_NAMES.elementMorphHandoff}\`, which needs that badge). Costs set in a
    badge that "shrank" squash down and, still shrinking, the badge closes around the smaller
    figure. Subscribers that "doubled" put out a copy beside themselves, and the pair slides
    together into "4M".
  - **Never a scale-and-fade swap.** Bare text shrinking out while other bare text grows in is
    a crossfade, not a morph, whatever the plan calls it.
- **A relationship needs both parties.** For that beat the pair is the one element (the two
  names or objects set together), and the gesture is how they move against each other. Then
  the next beat takes the frame back to one element.

## Timing
- **The key moment lands on the verb**: the peak of a lift, the contact of a drop, the moment
  of a launch, the instant two things part. The wind-up starts before it; the settle plays out
  after it, during the hold.
- **One gesture, under about 0.8s**, so it reads inside a beat. Two gestures at once, or a
  gesture that tells a little story, is too much for one second.
- **The principle skills set the numbers.** A drop uses \`${SKILL_NAMES.massAndWeight}\`'s table, a
  launch \`${SKILL_NAMES.anticipationWindUp}\`'s 3:1:8, an arc \`${SKILL_NAMES.arcPathMotion}\`'s
  curves. Load the ones your gestures use and build to them.

## Directions mean what viewers expect
Up is more or better, down is less or worse. Right is forward, later or progress, and left is
back, earlier or behind. Apart is splitting, together is joining, in front is leading. Exits
carry meaning too: an element whose line said it fell does not leave upward.

## Pitfalls
- **Every positive line becomes the same rise-in.** That is no different from an ordinary
  arrival. Use the strength tables.
- **The gesture lands on the subject's word.** It belongs on the verb, so it often belongs in
  the handoff to the next beat.
- **Illustrating the noun instead of the action.** A drawn object for the subject is not the
  action; the action is what the subject does.
- **Meaning ignored for variety.** If two lines both say something went up, both go up. Vary
  how hard, not the direction.
- **A gesture that reads as decoration**: a wobble or a spin that the line does not say. Every
  acted move has a word it answers to.

## Write it in the plan
On the phase: the words, then what the move shows, then the skill that builds it ("'costs
fell': the figure drops from above and lands heavy: mass and weight"). A verb spoken between
two beats goes on the second beat's phase, in its \`transforms\` ("'downloads shot up': the word
winds up and launches out of the top; '2M' drops back in on the same line: velocity-matched
cut").`;
    },
};
